import React, { ChangeEvent, useEffect } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import {
  ButtonBase,
  Grid,
  MenuItem,
  Typography,
  Link as MUILink,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core'
import Box from '@material-ui/core/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import {
  ListKits,
  ListWarning,
  MilliplexKitPrice,
} from '@src/types/graphql-types'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import LiquidFormLabel from '@src/components/LiquidFormLabel'
import LiquidSelect from '@src/components/LiquidSelect'
import WarningIcon from '@material-ui/icons/Warning'
import {
  SampleTypeYesNoOptions,
  MilliplexInstrumentTypeList,
  AssayTypeOptions,
  ActiveLinkType,
} from '../../enum'
import parse from 'html-react-parser'
import { SelectedDataType } from './DesignAndPriceModal'
import { MilliplexFormValues } from '@src/routes/Configurators/products/Milliplex/Milliplex'
import { useFormikContext } from 'formik'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import HoverToolTip from '@src/components/HoverToolTip'
import HandleMarkup from '@src/components/HandleMarkup'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import { useRouter } from 'next/router'
import { Skeleton } from '@material-ui/lab'
import { parseParams } from '../../Step2/SearchResult/ProductCard/ProductCard'
import AnalyteInfoMessage from '../AnalyteInfoMessage'

const useStyles = makeStyles((theme: Theme) => {
  const cellBorder = `0.5px solid ${theme.palette.grey[500]}`
  const selectedCellBorder = `0.5px solid ${theme.palette.primary.dark}`
  return {
    analyteCellContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(3),
      borderRight: cellBorder,
      borderBottom: cellBorder,
      '&:nth-child(3n+1)': {
        borderLeft: cellBorder,
      },
      '&:nth-child(-n+3)': {
        borderTop: cellBorder,
      },
    },
    selectedAnalyteCellContainer: {
      display: 'flex',
      padding: theme.spacing(3),
      borderRight: selectedCellBorder,
      borderBottom: selectedCellBorder,
      '&:nth-child(3n+1)': {
        borderLeft: selectedCellBorder,
      },
      '&:nth-child(-n+3)': {
        borderTop: selectedCellBorder,
      },
      backgroundColor: theme.palette.primary.light,
    },

    checkboxRoot: {
      paddingBottom: theme.spacing(1),
      alignSelf: 'flex-start',
    },
    tableWrapper: {
      marginTop: theme.spacing(2),
    },
    analyteHeaders: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: theme.spacing(4),
    },
    selectAllDeselectAllButtonContainer: {
      display: 'flex',
      '& > :first-child': {
        borderRight: `1px solid ${theme.palette.grey[500]}`,
        paddingRight: theme.spacing(4),
      },
      '& > :nth-child(2)': {
        paddingLeft: theme.spacing(4),
      },
    },
    buttonStyle: {
      color: theme.palette.primary.dark,
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightBold,
    },
    headerText: {
      fontWeight: theme.typography.fontWeightBold,
    },
    premixedInfoContainer: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    customizeText: {
      fontWeight: theme.typography.fontWeightBold,
      padding: theme.spacing(5, 0, 0),
    },
    additionalCustomizeText: {
      fontWeight: theme.typography.fontWeightBold,
      padding: theme.spacing(5, 0),
    },
    designAndPriceText: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(14),
    },
    designAndPriceValue: {
      fontWeight: theme.typography.fontWeightBold,
      fontSize: theme.typography.pxToRem(18),
    },
    warningIcon: {
      color: theme.palette.warning.main,
      marginRight: theme.spacing(2),
    },
    sampleTypeTextContainer: {
      display: 'flex',
      marginTop: theme.spacing(5),
    },
    otherWarning: {
      marginTop: theme.spacing(5),
    },
    sampleTypeContainer: {
      borderTop: cellBorder,
      marginTop: theme.spacing(6),
    },
    selectBoxStyle: {
      width: 205,
      marginBottom: theme.spacing(6),
    },
    sampleTypeSelect: {
      width: 205,
      marginTop: theme.spacing(4),
    },
    analytesDisabledIcon: {
      display: 'flex',
      alignItems: 'center',
    },
    additionalOptionLabel: {
      display: 'flex',
    },
    infoIcon: {
      fontSize: theme.typography.pxToRem(16),
      color: theme.palette.primary.main,
      cursor: 'pointer',
      marginLeft: theme.spacing(1.25),
    },
    instrumentTypesContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      marginLeft: theme.spacing(-3),
      justifyContent: 'space-between',
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,
        marginRight: theme.spacing(2.5),
      },
    },
    checkboxContainer: {
      display: 'flex',
      gap: theme.spacing(2),
    },
    instWrapper: {
      borderBottom: cellBorder,
      paddingBlock: theme.spacing(4),
    },
    formControlLabelStyle: {
      width: '95%',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    checkBoxWrapper: {
      '& label': {
        alignItems: 'center',
        '& .MuiIconButton-root': {
          paddingBottom: theme.spacing(2.25),
        },
      },
    },
  }
})

interface NonPremixedProductProps {
  selectedAnalytes: string[]
  setSelectedAnalytes: React.Dispatch<React.SetStateAction<string[]>>
  milliplexKit: ListKits
  handleAnalyteClick: (analyte: string) => void
  setCustomPremixing: React.Dispatch<React.SetStateAction<string>>
  setSpaceSaver: React.Dispatch<React.SetStateAction<string>>
  customPremixing: string
  spaceSaver: string
  sampleType: string
  enabledAnalytesList: string[]
  sampleTypeList: string[]
  handleSampleTypeSelection: (sampleType: string) => void
  listWarnings: ListWarning[]
  fetchKitDetails: (params: SelectedDataType) => void
  priceData: MilliplexKitPrice
  setSelectedInstrument: React.Dispatch<React.SetStateAction<string>>
  selectedInstrument: string
  selectInitialAllAnalytes: boolean
  setInitialSelectAllAnalytes: React.Dispatch<React.SetStateAction<boolean>>
  loading: boolean
  radioButtonClicked: boolean
  setRadioButtonClicked: React.Dispatch<React.SetStateAction<boolean>>
}

const NonPremixedProduct: React.FC<NonPremixedProductProps> = ({
  selectedAnalytes,
  setSelectedAnalytes,
  handleAnalyteClick,
  milliplexKit,
  setCustomPremixing,
  setSpaceSaver,
  customPremixing,
  spaceSaver,
  sampleType,
  enabledAnalytesList,
  sampleTypeList,
  handleSampleTypeSelection,
  listWarnings,
  fetchKitDetails,
  priceData,
  setSelectedInstrument,
  selectedInstrument,
  selectInitialAllAnalytes,
  setInitialSelectAllAnalytes,
  loading,
  radioButtonClicked,
  setRadioButtonClicked,
}) => {
  const classes = useStyles()
  const additionalOptions = [
    SampleTypeYesNoOptions.Yes,
    SampleTypeYesNoOptions.No,
  ]

  const { isDealer, currentUser } = useCurrentUser()
  const isAuUser =
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.metadata?.country &&
    currentUser.metadata?.country === 'AU'

  const renderedValue = (value) => {
    if (value === '') return 'Select'
    return value
  }

  const formik = useFormikContext<MilliplexFormValues>()
  const router = useRouter()
  const previouslyViewedKit =
    router.query.previouslyViewed || formik.values.previouslyViewed
  const isPanelPreviouslyViewedAndIsPlexpedition =
    previouslyViewedKit === milliplexKit.panelId &&
    milliplexKit.assayTypeSelection === AssayTypeOptions.Screening

  const isCustomPremixAvailable = milliplexKit?.customPremixedAvailable?.some(
    (item) =>
      item.toLocaleLowerCase() === SampleTypeYesNoOptions.Yes.toLowerCase()
  )
  const isSpaceSaverAvailable =
    milliplexKit?.spaceSaver?.toLowerCase() ===
    SampleTypeYesNoOptions.Yes.toLowerCase()
  const handleSelectAllAnalytes = () => {
    setRadioButtonClicked(false)
    setInitialSelectAllAnalytes(false)
    setSelectedAnalytes(enabledAnalytesList)
    fetchKitDetails({ analytes: enabledAnalytesList })
  }

  const fetchKitDetailsWithDefaultSelection = () => {
    if (previouslyViewedKit === milliplexKit.panelId) {
      setSelectedAnalytes([])
      fetchKitDetails({ analytes: [] })
    } else {
      let defaultSelection
      if (router.query.activeLink === ActiveLinkType.SelectKits) {
        defaultSelection = formik.values.analytes.filter((analyte) =>
          enabledAnalytesList.includes(analyte)
        )
      } else {
        const kit = formik.values.selectedKits[milliplexKit.panelId]
        const selectedKit = kit
          ? parseParams({
              [milliplexKit.panelId]: kit.url,
            })[0]
          : null
        defaultSelection = selectedKit?.defaultAnalytes
      }
      setSelectedAnalytes(defaultSelection)
      fetchKitDetails({ analytes: defaultSelection })
    }
  }

  const handleDeselectAllAnalytes = () => {
    setRadioButtonClicked(false)
    setInitialSelectAllAnalytes(false)
    fetchKitDetailsWithDefaultSelection()
  }
  const handleCustomPremixing = (e) => {
    setRadioButtonClicked(false)
    setInitialSelectAllAnalytes(false)
    setCustomPremixing(e.target.value as string)
    fetchKitDetails({ customPremixing: e.target.value as string })
  }

  const instrumentRadioTypeChange = (event: ChangeEvent<{}>): void => {
    setRadioButtonClicked(true)
    const instrumentSelected = (event.target as HTMLInputElement).value
    setSelectedInstrument(instrumentSelected)
    fetchKitDetailsWithDefaultSelection()
    instrumentSelected &&
      fetchKitDetails({
        instrumentTypes: [instrumentSelected],
      })
  }

  useEffect(() => {
    selectInitialAllAnalytes && setSelectedAnalytes(enabledAnalytesList)
  }, [enabledAnalytesList])
  const instrumentCheckedStatus = (instrument: string): boolean => {
    return instrument === selectedInstrument
  }
  const formatLabel = (label: string): string => {
    return label.replaceAll('®', '<sup>®</sup>')
  }

  const AnalytesSkeleton = () => (
    <Grid container spacing={2} className={classes.tableWrapper}>
      {[...Array(24)].map((_, index) => (
        <Grid item xs={4} key={index} className={classes.analyteCellContainer}>
          <Grid item xs={12}>
            <Skeleton variant="rect" height={50} />
          </Grid>
        </Grid>
      ))}
    </Grid>
  )
  const displayLoader =
    milliplexKit.assayTypeSelection === AssayTypeOptions.Screening &&
    loading &&
    radioButtonClicked
  return (
    <Box>
      {isPanelPreviouslyViewedAndIsPlexpedition ? (
        <Box className={classes.instWrapper}>
          {' '}
          <Typography className={classes.headerText}>
            <FormattedMessage
              id="SELECT_INSTRUMENTS"
              defaultMessage="Select Instruments(s)"
            />
          </Typography>
          <RadioGroup
            aria-label="selectedInstrument"
            name="selectedInstrument"
            row
          >
            <Box className={classes.instrumentTypesContainer}>
              {MilliplexInstrumentTypeList.sort().map((instrument) => {
                return (
                  <FormControlLabel
                    key={instrument}
                    aria-label="brand"
                    value={instrument}
                    onChange={instrumentRadioTypeChange}
                    checked={instrumentCheckedStatus(instrument)}
                    control={<StyledRadio isConfiguratorsRedesign="true" />}
                    label={<HandleMarkup value={formatLabel(instrument)} />}
                  />
                )
              })}
            </Box>
          </RadioGroup>
        </Box>
      ) : null}
      {displayLoader ? (
        <AnalytesSkeleton />
      ) : (
        <>
          <Box className={classes.analyteHeaders}>
            <Box>
              <Typography className={classes.headerText}>
                <FormattedMessage
                  id="PC_ANALYTES_milliplex"
                  defaultMessage="Select Analytes"
                />
                {milliplexKit?.minimumAnalytesCount &&
                  milliplexKit.minimumAnalytesCount > 1 && (
                    <>
                      &nbsp;
                      <FormattedMessage
                        id="MILLIPLEX_MINIMUM_ANALYTES_MESSAGE"
                        defaultMessage="(Minimum of {count})"
                        values={{ count: milliplexKit.minimumAnalytesCount }}
                      />
                    </>
                  )}
              </Typography>
            </Box>
            <Box className={classes.selectAllDeselectAllButtonContainer}>
              <ButtonBase onClick={handleSelectAllAnalytes}>
                <Typography className={classes.buttonStyle}>
                  <FormattedMessage
                    id="SELECT_ALL"
                    defaultMessage="Select All"
                  />
                </Typography>
              </ButtonBase>

              <ButtonBase onClick={handleDeselectAllAnalytes}>
                <Typography className={classes.buttonStyle}>
                  <FormattedMessage
                    id="DESELECT_ALL"
                    defaultMessage="Deselect All"
                  />
                </Typography>
              </ButtonBase>
            </Box>
          </Box>
          <Grid container className={classes.tableWrapper}>
            {milliplexKit?.analytes
              .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
              .map((analyte) => {
                let checked
                let defaultSelection
                const disabled = !enabledAnalytesList.includes(analyte)

                if (previouslyViewedKit === milliplexKit.panelId) {
                  defaultSelection = false
                  checked = selectedAnalytes.includes(analyte)
                } else {
                  if (router.query.activeLink === ActiveLinkType.SelectKits) {
                    defaultSelection = formik.values.analytes.includes(analyte)
                    checked = selectedAnalytes.includes(analyte)
                  } else {
                    const kit = formik.values.selectedKits[milliplexKit.panelId]
                    const selectedKit = kit
                      ? parseParams({
                          [milliplexKit.panelId]: kit.url,
                        })[0]
                      : null
                    if (selectedKit?.defaultAnalytes?.includes(analyte)) {
                      checked = true
                    } else {
                      checked = selectedAnalytes.includes(analyte)
                    }
                    defaultSelection =
                      selectedKit?.defaultAnalytes?.includes(analyte)
                  }
                }
                return (
                  <Grid
                    item
                    xs={4}
                    key={analyte}
                    className={
                      checked
                        ? classes.selectedAnalyteCellContainer
                        : classes.analyteCellContainer
                    }
                  >
                    <Grid
                      item
                      xs={disabled ? 11 : 12}
                      className={classes.checkBoxWrapper}
                    >
                      <LiquidCheckbox
                        aria-label={analyte}
                        value={analyte}
                        checked={checked}
                        classes={{
                          root: classes.checkboxRoot,
                        }}
                        formControlClasses={{
                          label: classes.formControlLabelStyle,
                        }}
                        onChange={() => handleAnalyteClick(analyte)}
                        label={analyte}
                        disabled={disabled || defaultSelection}
                      />
                    </Grid>
                  </Grid>
                )
              })}
          </Grid>
        </>
      )}
      <AnalyteInfoMessage analytes={milliplexKit?.analytes || []} />

      <Grid container className={classes.sampleTypeContainer}>
        {sampleTypeList.length > 1 && (
          <Grid item xs={12}>
            <Typography className={classes.customizeText}>
              <FormattedMessage
                id="CONF_SAMPLE_TYPE"
                defaultMessage="Sample Type"
              />
            </Typography>
          </Grid>
        )}
        <>
          {listWarnings && listWarnings.length > 0 ? (
            <Grid item xs={12}>
              <Box className={classes.sampleTypeTextContainer}>
                <WarningIcon className={classes.warningIcon} />
                <Typography>
                  {parse(
                    listWarnings?.find(
                      (item) => item?.key === 'sampleTypeWarning'
                    )?.value || ''
                  )}
                  &nbsp;
                  <FormattedMessage
                    id="MILLIPLEX_SAMPLE_TYPE_TEXT"
                    defaultMessage="For questions, contact "
                  />
                  <MUILink href="mailto:cssorders@sial.com">
                    <FormattedMessage
                      id="TECHNICAL_SERVICE"
                      defaultMessage="Technical Service"
                    />
                  </MUILink>
                  .
                </Typography>
              </Box>
            </Grid>
          ) : null}
          {milliplexKit?.requireSample &&
            sampleTypeList &&
            sampleTypeList.length > 0 && (
              <Grid item xs={12}>
                <LiquidSelect
                  filled
                  onChange={(e) =>
                    handleSampleTypeSelection(e.target.value as string)
                  }
                  value={sampleType}
                  className={classes.sampleTypeSelect}
                  renderValue={renderedValue}
                  displayEmpty
                >
                  {['Select'].concat(sampleTypeList).map((value) => (
                    <MenuItem key={value} value={value}>
                      <FormattedMessage
                        id={`MILLIPLEX_${value}`}
                        defaultMessage={value}
                      />
                    </MenuItem>
                  ))}
                </LiquidSelect>
              </Grid>
            )}
        </>

        <Grid item xs={12}>
          <Typography className={classes.additionalCustomizeText}>
            <FormattedMessage
              id="MILLIPLEX_ADDITIONAL_OPTIONS"
              defaultMessage="Additional Options"
            />
          </Typography>
        </Grid>

        <Grid item xs={4}>
          <Box className={classes.selectBoxStyle}>
            <LiquidFormLabel>
              <div className={classes.additionalOptionLabel}>
                <FormattedMessage
                  id="CONF_MILLIPLEX_CUSTOM_PREMIX_LABEL_milliplex"
                  defaultMessage="Custom Premixing"
                />
                <HoverToolTip
                  fontSize="14px"
                  toolTipText={
                    <FormattedMessage
                      id="CONF_MILLIPLEX_CUSTOM_PREMIX_ToolTip_Text"
                      defaultMessage="All selected beads will be premixed before the kit is shipped."
                    />
                  }
                />
              </div>
            </LiquidFormLabel>
            {isCustomPremixAvailable ? (
              <LiquidSelect
                filled
                onChange={handleCustomPremixing}
                value={customPremixing}
                displayEmpty
                renderValue={renderedValue}
              >
                {additionalOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    <FormattedMessage
                      id={`MILLIPLEX_${value}`}
                      defaultMessage={value}
                    />
                  </MenuItem>
                ))}
              </LiquidSelect>
            ) : (
              '_ _'
            )}
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box className={classes.selectBoxStyle}>
            <LiquidFormLabel>
              <div className={classes.additionalOptionLabel}>
                <FormattedMessage
                  id="CONF_MILLIPLEX_SPACE_SAVER_LABEL_milliplex"
                  defaultMessage="Space Saver"
                />
                <HoverToolTip
                  fontSize="14px"
                  toolTipText={
                    <FormattedMessage
                      id="CONF_MILLIPLEX_SpaceSaver_ToolTip_Text"
                      defaultMessage="Reduce packaging from boxes to plastic bags."
                    />
                  }
                />
              </div>
            </LiquidFormLabel>
            {isSpaceSaverAvailable ? (
              <LiquidSelect
                onChange={(e) => setSpaceSaver(e.target.value as string)}
                value={spaceSaver}
                displayEmpty
                renderValue={renderedValue}
              >
                {additionalOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    <FormattedMessage
                      id={`MILLIPLEX_${value}`}
                      defaultMessage={value}
                    />
                  </MenuItem>
                ))}
              </LiquidSelect>
            ) : (
              '_ _'
            )}
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography className={classes.designAndPriceText}>
            <FormattedMessage
              id="MILLIPLEX_ADDITIONAL_PRICE"
              defaultMessage="Price"
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {!isDealer && isAuUser ? (
            <FormattedMessage id="QUOTE_ITEM" defaultMessage="Quote Item" />
          ) : (
            <>
              {priceData &&
              priceData?.price &&
              selectedAnalytes.length &&
              !isNaN(parseFloat(priceData?.price)) &&
              parseFloat(priceData?.price) > 0 ? (
                <FormattedNumber
                  style="currency"
                  currencyDisplay="symbol"
                  currency={priceData.currency as string}
                  value={parseFloat(priceData?.price)}
                />
              ) : (
                <span>&#8212;</span>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}
export default NonPremixedProduct
